import { __read, __assign, __awaiter, __generator } from 'tslib';
import React__default, { useMemo, useState, useCallback } from 'react';
import classNames from 'classnames';
import { useSearchAPI } from '../../../../context/NavigationConfigContext.esm.js';
import debounce from 'lodash/debounce';
import { useTextCopy } from '@invitae/cms-blocks';
import { NvInputSearch } from '@invitae/ids-react';
import { useAnalyticsQueue } from '@invitae/nucleobase-analytics';
import { useCountryLocationPrefix } from '@invitae/nucleobase-engine-contentful';
import { ResultsList } from './ResultsList/ResultsList.esm.js';
import styles from './TestCatalogSearch.module.esm.js';

var SEARCH_QUERY_ANALYTICS_EVENT = 'enter search query in search bar';
var CLICK_SEARCH_RESULT_LINK_ANALYTICS_EVENT = 'click search result link in search bar';
var TestCatalogSearch = function (_a) {
    var _b;
    var className = _a.className, _c = _a.isDefaultSuggestionsEnabled, isDefaultSuggestionsEnabled = _c === void 0 ? true : _c, searchQueryAnalyticsEvent = _a.searchQueryAnalyticsEvent, searchResultLinkClickAnalyticsEvent = _a.searchResultLinkClickAnalyticsEvent, _d = _a.defaultSuggestions, defaultSuggestions = _d === void 0 ? [] : _d, _e = _a.resultListType, resultListType = _e === void 0 ? 'flat' : _e;
    var defaultSearchResult = useMemo(function () { return (isDefaultSuggestionsEnabled ? defaultSuggestions : []); }, [isDefaultSuggestionsEnabled, defaultSuggestions]);
    var getText = useTextCopy().getText;
    var defaultResultTitle = isDefaultSuggestionsEnabled ? getText('searchTitle') : '';
    var _f = __read(useState(''), 2), searchValue = _f[0], setSearchValue = _f[1];
    var _g = __read(useState(defaultSearchResult), 2), searchResult = _g[0], setSearchResult = _g[1];
    var _h = __read(useState(false), 2), isLoading = _h[0], setIsLoading = _h[1];
    var addCountryCodePrefix = useCountryLocationPrefix().addCountryCodePrefix;
    var logEvent = useAnalyticsQueue().logEvent;
    var goToSearchPage = function () {
        if (searchValue) {
            window.location.assign(addCountryCodePrefix("/search?q=".concat(searchValue)));
        }
    };
    var handleResultAnalyticsEvent = useCallback(function (clickThroughLink) {
        var _a = searchResultLinkClickAnalyticsEvent || {}; _a.eventName; var eventProperties = _a.eventProperties;
        logEvent({
            eventName: CLICK_SEARCH_RESULT_LINK_ANALYTICS_EVENT ,
            eventProperties: __assign({ 'click through link': clickThroughLink }, eventProperties),
        });
    }, [logEvent, searchResultLinkClickAnalyticsEvent]);
    var handleSearchQueryAnalyticsEvent = useCallback(function (searchQuery) {
        var _a = searchQueryAnalyticsEvent || {}, eventName = _a.eventName, eventProperties = _a.eventProperties;
        logEvent({
            eventName: eventName || SEARCH_QUERY_ANALYTICS_EVENT,
            eventProperties: __assign({ query: searchQuery }, eventProperties),
        });
    }, [logEvent, searchQueryAnalyticsEvent]);
    var searchAPI = useSearchAPI();
    var search = useCallback(function (searchValue) { return __awaiter(void 0, void 0, void 0, function () {
        var response, initializationConfig, error_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!searchValue) {
                        setSearchResult(defaultSearchResult);
                        setIsLoading(false);
                        return [2 /*return*/];
                    }
                    if (!searchAPI) {
                        throw Error('Please provide config via NucleobaseConfigContext');
                    }
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 4, 5, 6]);
                    return [4 /*yield*/, fetch("".concat(searchAPI, "?query=").concat(searchValue))
                        // handles error case where a HTML document is returned instead of JSON
                    ];
                case 2:
                    response = _a.sent();
                    // handles error case where a HTML document is returned instead of JSON
                    if (response.status === 500) {
                        setSearchResult([]);
                        return [2 /*return*/];
                    }
                    return [4 /*yield*/, response.json()];
                case 3:
                    initializationConfig = _a.sent();
                    setSearchResult(initializationConfig.results);
                    return [3 /*break*/, 6];
                case 4:
                    error_1 = _a.sent();
                    setSearchResult([]);
                    throw Error("Error at fetch: ".concat(error_1));
                case 5:
                    setIsLoading(false);
                    return [7 /*endfinally*/];
                case 6: return [2 /*return*/];
            }
        });
    }); }, [searchAPI, defaultSearchResult]);
    var onChange = useCallback(function (value) {
        handleSearchQueryAnalyticsEvent(value);
        search(value);
    }, [search, handleSearchQueryAnalyticsEvent]);
    var debounceSearch = useCallback(debounce(onChange, 300), [onChange]);
    var handleSearch = useCallback(function (value) {
        setIsLoading(true); //moved it here so loading animation starts as soon as the user is typing
        debounceSearch(value);
        setSearchValue(value);
    }, [debounceSearch]);
    var resetSearch = function () {
        setSearchValue('');
        setSearchResult([]);
    };
    return (React__default.createElement("div", { className: classNames(styles.root, className === null || className === void 0 ? void 0 : className.root) },
        React__default.createElement("div", { className: className === null || className === void 0 ? void 0 : className.inputWrapper },
            React__default.createElement("h3", { className: classNames('nv-mh4', styles.searchTitle) }, getText('searchHeading')),
            React__default.createElement(NvInputSearch, { buttonSubmitAriaLabel: "Focus search", classNames: className === null || className === void 0 ? void 0 : className.input, "data-cy": "nav_search-input", isLoading: isLoading, label: "Search", onChangeInput: function (value) { return handleSearch(value); }, onClearInput: resetSearch, onSearch: goToSearchPage, placeholder: getText('searchPlaceholder'), showPrefixElement: true })),
        !isLoading && searchValue && (React__default.createElement("div", { className: classNames(className === null || className === void 0 ? void 0 : className.result, (_b = {},
                _b[styles.popup] = resultListType === 'popup',
                _b)), onBlur: function (e) { return e.stopPropagation(); } },
            React__default.createElement(ResultsList, { handleAnalyticsEvent: handleResultAnalyticsEvent, searchResult: searchResult, searchValue: searchValue, title: searchValue ? getText('didYouMean') : defaultResultTitle })))));
};

export { TestCatalogSearch as default };
//# sourceMappingURL=TestCatalogSearch.esm.js.map
